import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { useParams } from "react-router-dom";
import {
  labelExportAsync,
  selectCardsList,
  selectCardsListStatus,
  selectShipmentsList,
} from "./cardsSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  usePersistMuiTableState,
  dateFormatterDayMonthYearLong2Digits,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  ButtonDS,
  Spacer,
  DownloadOutlined,
  ArrowForwardOutlined,
  dateFormatterDayMonthYear,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import {
  CardsListWithShipment,
  CardsListWithShipmentDisplayed,
} from "./cardsApi";
import { getDate, formatDate } from "../../lib/materialUiFormats";
import styled from "styled-components";

export const ShipmentsTab = () => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const shipmentsList = useAppSelector(selectShipmentsList);
  const cardsList = useAppSelector(selectCardsList);
  const cardsListStatus = useAppSelector(selectCardsListStatus);
  const [listDisplayed, setListDisplayed] = useState<
    CardsListWithShipmentDisplayed[]
  >([]);

  const cardsListWithShipments: CardsListWithShipment[] = useMemo(() => {
    const filteredCards = cardsList.filter((card) => card.shipment !== null);
    return filteredCards
      .map((card) => {
        const currentShipment = shipmentsList.find(
          (shipment) => shipment.uuid === card.shipment,
        );
        if (!currentShipment) return;
        return {
          ...card,
          shipmentDetails: ` ${dateFormatterDayMonthYear(new Date(currentShipment.requestedAt))} - ${t(`shipments.status.${currentShipment.status}`)} - ${currentShipment.providerId ?? ""} - ${currentShipment.address} `,
          shipmentLink: currentShipment.bucketFileName,
          shipmentUuid: currentShipment.uuid,
          requestedAt: currentShipment.requestedAt,
          shipmentStatus: currentShipment.status,
          errorMessage: currentShipment.errorMessage,
          providerId: currentShipment.providerId,
        };
      })
      .filter((card) => card !== undefined)
      .sort((a, b) => {
        return (
          new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
        );
      });
  }, [cardsList, shipmentsList, t]);

  const pageName = "shipments";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      registrationNumber: "size",
      vehicleReference: "size",
      rfid: "size",
      status: "size",
      address: "size",
      createdAt: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof CardsListWithShipment, value: string) => {
      switch (key) {
        case "status":
          return t(`cards.status.${value}`);
        case "requestedAt":
          return dateFormatterDayMonthYearLong2Digits(new Date(value));
        case "shipmentStatus":
          return t(`shipments.status.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "status",
        "createdAt",
        "shipmentStatus",
      ] as (keyof CardsListWithShipmentDisplayed)[],
    [],
  );

  const cardsListWithId: (CardsListWithShipmentDisplayed & { id: string })[] =
    useMemo(() => {
      const rowsWithId = cardsListWithShipments.map((card) => {
        let newDict: CardsListWithShipment & {
          id: string;
        } = {
          ...card,
          id: card.uuid,
        };
        Object.entries(card).forEach(([key, value]) => {
          if (
            keysToTranslate.includes(
              key as keyof CardsListWithShipmentDisplayed,
            )
          ) {
            newDict = {
              ...newDict,
              [key]: findKeysToTranslate(
                key as keyof CardsListWithShipmentDisplayed,
                value as string,
              ),
            };
          }
        });
        return newDict;
      });
      return rowsWithId;
    }, [cardsListWithShipments, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (
      cardsListWithId &&
      cardsListWithShipments &&
      cardsListStatus === "success"
    ) {
      setListDisplayed(cardsListWithId);
    }
  }, [cardsListWithId, cardsListWithShipments, cardsListStatus]);

  const searchBarProps = {
    values: cardsListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const columnsDisplayed: {
    [index in keyof CardsListWithShipmentDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      registrationNumber: {
        type: "string",
        width: 170,
      },
      vehicleReference: {
        type: "string",
        width: 200,
      },
      identifier: {
        type: "string",
      },
      rfid: {
        type: "string",
        width: 120,
      },
      last4Digits: {
        type: "string",
        width: 120,
      },
      status: {
        type: "string",
        width: 150,
      },
      shipmentUuid: {
        type: "string",
        width: 350,
      },
      shipmentStatus: {
        type: "string",
        width: 150,
      },
      errorMessage: {
        type: "string",
        width: 300,
      },
      shipmentDetails: {
        type: "string",
        width: 750,
      },
      shipmentLink: {
        type: "string",
        width: 300,
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledFlex>
                <ButtonDS
                  format={"hug"}
                  buttonType={"secondary"}
                  leftIcon={<DownloadOutlined />}
                  onClick={() => {
                    void dispatch(
                      labelExportAsync({
                        uuid: value,
                      }),
                    );
                  }}
                  text={t("shipments.download.button")}
                />
                <Spacer x={1.5} />
                {row["providerId"] && (
                  <ButtonDS
                    format={"hug"}
                    buttonType={"secondary"}
                    leftIcon={<ArrowForwardOutlined />}
                    onClick={() =>
                      window.open(
                        `https://www.laposte.fr/outils/suivre-vos-envois?code=${row["providerId"]}`,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                    text={t("shipments.link.button")}
                  />
                )}
              </StyledFlex>
            )
          );
        },
      },
      requestedAt: {
        type: "string",
        width: 150,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
    };
  }, [dispatch, t]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "shipments") {
    return;
  }
  return (
    <MaterialUiTable<
      keyof CardsListWithShipmentDisplayed,
      CardsListWithShipmentDisplayed
    >
      rows={listDisplayed}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      groupingColumn={["shipmentDetails"]}
      groupingFirstColumnDisplayed={"shipmentLink"}
    />
  );
};

const StyledFlex = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
`;
